import * as React from "react";
import { useState } from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";

import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";

import { useDebounce } from "hooks";

import AutocompleteFormField from "components/autocomplete-form-field";
import { PATIENTS_SEARCH_QUERY } from "components/patient-search/patient-search.component";
import {
  RECIPIENT_OBJECT_TYPES,
  RECIPIENT_PATIENT_OBJECT_TYPE,
  RECIPIENT_PHYSICIAN_OBJECT_TYPE
} from "constants/emails";
import { useQueryPatientsQuery } from "services/patients";
import { useGetPhysiciansQuery } from "services/physicians";

import { formatRecipient } from "./email.utils";

export type Recipient = {
  id: number;
  email: string;
  name: string;
  avatar: string;
  type:
    | typeof RECIPIENT_PATIENT_OBJECT_TYPE
    | typeof RECIPIENT_PHYSICIAN_OBJECT_TYPE;
};

type RecipientsFieldProps = {
  label?: string;
  afterOptionSelected?: (value: Array<Recipient | string>) => void;
};

export type RhfRecipientsFieldProps<T extends FieldValues> =
  RecipientsFieldProps & UseControllerProps<T>;

export default function RecipientsField<T extends FieldValues>({
  control,
  name,
  afterOptionSelected,
  label = "To"
}: RhfRecipientsFieldProps<T>) {
  const [search, setSearch] = useState<string>("");
  const { value: dSearch, isDebouncing } = useDebounce(search, 200);
  const { data: searchPatients, isFetching: isFetchingForSearch } =
    useQueryPatientsQuery({
      search: dSearch && dSearch.length >= 3 ? dSearch : undefined,
      baseFilter: {},
      is_active: true,
      query: PATIENTS_SEARCH_QUERY
    });
  const { data: physicians, isLoading: isLoadingPhysicians } =
    useGetPhysiciansQuery({
      is_active: true,
      search: dSearch
    });
  const isLoading = isFetchingForSearch || isLoadingPhysicians;
  const options = [
    ...(searchPatients?.flatMap((patient) => {
      const recipients: Recipient[] = [];
      const searchLower = dSearch?.toLowerCase() || "";
      const primaryEmail = patient.email?.toLowerCase() || "";
      const secondaryEmail = patient.secondary_email?.toLowerCase() || "";

      // Check if search text is in secondary email
      const isInSecondaryEmail =
        secondaryEmail && searchLower && secondaryEmail.includes(searchLower);
      // Check if search text is in primary email
      const isInPrimaryEmail =
        primaryEmail && searchLower && primaryEmail.includes(searchLower);

      // Show primary email option only if:
      // - Search text is NOT in secondary email, OR
      // - Search text is in BOTH primary and secondary email
      if (patient.email && (!isInSecondaryEmail || isInPrimaryEmail)) {
        const recipient = formatRecipient({ ...patient, email: patient.email });
        if (recipient) recipients.push(recipient);
      }

      return recipients;
    }) || []),
    ...(physicians
      ?.filter((physician) => Boolean(physician.email))
      .map((physician) => formatRecipient(physician) as Recipient) || [])
  ];
  return (
    <AutocompleteFormField
      name={name}
      label={label}
      control={control}
      sx={{ ml: 0 }}
      required
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.name
      }
      isOptionEqualToValue={(option, value) => {
        const optionTitle = typeof option === "string" ? option : option.email;
        const valueTitle = typeof value === "string" ? value : value.email;
        return optionTitle === valueTitle;
      }}
      options={options}
      multiple
      freeSolo
      autoFocus
      filterOptions={(options) => options}
      isLoading={isLoading || isDebouncing}
      onTextInputChange={(text) => {
        setSearch(text);
      }}
      onOptionSelected={(value) => {
        if (typeof value === "object" && Array.isArray(value)) {
          afterOptionSelected?.(value);
        } else if (typeof value === "string" && value) {
          afterOptionSelected?.([value]);
        } else {
          afterOptionSelected?.([]);
        }
      }}
      groupBy={(option: Recipient) =>
        RECIPIENT_OBJECT_TYPES.getFromCode(option.type)
      }
      renderTags={(value: readonly (Recipient | string)[], getTagProps) =>
        value.map((option: Recipient | string, index: number) => (
          <Tooltip
            title={typeof option === "string" ? option : option?.email}
            key={typeof option === "string" ? option : option?.email}
          >
            <Chip
              variant="outlined"
              color={
                typeof option === "string"
                  ? undefined
                  : option.type === RECIPIENT_PATIENT_OBJECT_TYPE
                    ? "info"
                    : "primary"
              }
              label={typeof option === "string" ? option : option.name}
              avatar={
                <Avatar
                  alt={
                    typeof option === "string"
                      ? option
                      : option.avatar || option.name
                  }
                  src={typeof option === "string" ? "" : option.avatar || ""}
                />
              }
              {...getTagProps({ index })}
            />
          </Tooltip>
        ))
      }
      renderOption={(props: object, option: Recipient) => (
        <ListItem {...props} dense>
          <ListItemAvatar>
            <Avatar
              alt={
                typeof option === "string"
                  ? option
                  : option.avatar || option.name
              }
              src={typeof option === "string" ? "" : option.avatar || ""}
            />
          </ListItemAvatar>
          <ListItemText secondary={option.email}>{option.name}</ListItemText>
        </ListItem>
      )}
    />
  );
}
